import dayjs from "dayjs";

import { IconAlertFill, IconPayment } from "@synthesis/shared-icons";
import { FileIconComponent, NoticeBase, Switch, Tag } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import type { ImportItemResult } from "@synthesis/shared-services/internal/application/types";

export type ImportResultAttachment = {
  id: string;
  fileName: string;
  isChanged?: boolean;
};

export type ImportResultStatusItem = {
  id: string;
  label: string;
  isChanged?: boolean;
};

export type ImportApplicationResultData = {
  outcome?: ImportItemResult["outcome"];
  number?: number | null;
  title?: string | null;
  typeLabel?: string | null;
  initiatorDisplay?: string | null;
  railway?: string | null;
  importedAt?: string | null;
  statuses: ImportResultStatusItem[];
  attachments: ImportResultAttachment[];
};

type ImportApplicationResultViewProps = {
  fileName?: string;
  data: ImportApplicationResultData;
  errors?: string[];
  isImportFailed?: boolean;
  showOnlyChanges: boolean;
  onShowOnlyChangesChange: (value: boolean) => void;
  variant?: "standalone" | "detail";
  className?: string;
};

const getFileExtension = (fileName: string) => {
  const extension = fileName.split(".").pop();

  return extension && extension !== fileName ? extension : "";
};

const formatNumber = (value: number | null | undefined) => (value != null ? `№${String(value)}` : "—");

const formatImportedAt = (value: string | null | undefined) => (value ? dayjs(value).format("DD.MM.YYYY, HH:mm") : "—");

const formatDisplayValue = (value: string | null | undefined) => (value?.trim() ? value : "—");

type ResultRowProps = {
  label: string;
  hasChanges?: boolean;
  isChanged?: boolean;
  showOnlyChanges: boolean;
  children: React.ReactNode;
};

const ResultRow = ({ label, hasChanges, isChanged, showOnlyChanges, children }: ResultRowProps) => {
  if (showOnlyChanges && !isChanged) {
    return null;
  }

  return (
    <div className="border-b-neutrals-90 flex items-start gap-4 border-b">
      <span className="paragraph-sm text-type-minor w-[7.5rem] shrink-0 whitespace-nowrap py-1">
        {label}
        {hasChanges ? (
          <span className="text-additional-green-50" aria-hidden>
            {" "}
            +
          </span>
        ) : null}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};

type TextValueProps = {
  children: React.ReactNode;
  isChanged?: boolean;
};

const TextValue = ({ children, isChanged }: TextValueProps) => (
  <div className={cn("paragraph-sm text-type-common py-1", isChanged && "bg-additional-green-90 px-1 py-1.5")}>{children}</div>
);

const StatusValueRow = ({ item }: { item: ImportResultStatusItem }) => {
  const Icon = item.isChanged ? IconPayment : IconAlertFill;

  return (
    <div className={cn("py-1.5 pl-1", item.isChanged && "bg-additional-green-90")}>
      <Tag
        size="sm"
        className="w-fit max-w-full"
        color={item.isChanged ? "olive" : undefined}
        status={item.isChanged ? undefined : "TO_REUPLOAD"}
        leftIcon={<Icon className="size-5 shrink-0" aria-hidden />}
      >
        {item.label}
      </Tag>
    </div>
  );
};

const AttachmentRow = ({ attachment }: { attachment: ImportResultAttachment }) => (
  <div className={cn("py-1", attachment.isChanged && "bg-additional-green-90")}>
    <div className="flex min-w-0 items-start gap-2">
      <FileIconComponent className="size-6 shrink-0" ext={getFileExtension(attachment.fileName)} />
      <span className="paragraph-sm text-type-common min-w-0 truncate">{attachment.fileName}</span>
    </div>
  </div>
);

type ImportApplicationErrorsViewProps = {
  errors?: string[];
  title?: string;
  className?: string;
};

const DEFAULT_IMPORT_ERROR_TITLE = "Не удалось импортировать заявку";

export const ImportApplicationErrorsView = ({ errors, title = DEFAULT_IMPORT_ERROR_TITLE, className }: ImportApplicationErrorsViewProps) => {
  if (!errors?.length) {
    return (
      <NoticeBase className={className} status="error" titleAppearance="common" title={title} description="Произошла ошибка при импорте заявки." />
    );
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {errors.map((error) => (
        <NoticeBase key={error} status="error" titleAppearance="common" title={title} description={error} />
      ))}
    </div>
  );
};

export const ImportApplicationResultView = ({
  fileName,
  data,
  errors,
  isImportFailed = false,
  showOnlyChanges,
  onShowOnlyChangesChange,
  variant = "standalone",
  className,
}: ImportApplicationResultViewProps) => {
  const hasErrors = isImportFailed || (errors?.length ?? 0) > 0;
  const changedStatuses = data.statuses.filter((status) => status.isChanged);
  const changedAttachments = data.attachments.filter((attachment) => attachment.isChanged);
  const hasStatusChanges = changedStatuses.length > 0;
  const hasAttachmentChanges = changedAttachments.length > 0;

  const visibleStatuses = showOnlyChanges ? changedStatuses : data.statuses;
  const visibleAttachments = showOnlyChanges ? changedAttachments : data.attachments;

  // typeLabel / initiatorDisplay / railway / importedAt — статические поля заявки, при импорте не меняются;
  // поэтому в режиме «Отобразить только изменения» они скрываются (isChanged не выставляется).
  // Номер/Название/Статус/Вложения — изменяемые: остаются видимыми (статус и вложения — только изменённые).
  const isContentChanged = data.outcome !== "NO_CHANGE" && data.outcome !== "FAILED";
  const hasAnyChanges = isContentChanged || hasStatusChanges || hasAttachmentChanges;

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {variant === "standalone" && fileName ? (
        <div className="flex flex-col gap-4">
          <h2 className="heading-h4 text-type-common">Архив заявки успешно импортирован</h2>
          <div className="flex min-w-0 items-start gap-3 py-2.5">
            <FileIconComponent className="size-6 shrink-0" ext="zip" />
            <span className="paragraph-base text-type-common min-w-0 break-all">{fileName}</span>
          </div>
        </div>
      ) : null}

      {hasErrors ? (
        <ImportApplicationErrorsView errors={errors} />
      ) : (
        <div className="flex flex-col gap-4">
          <Switch size="md" checked={showOnlyChanges} onCheckedChange={onShowOnlyChangesChange} label="Отобразить только изменения" />

          <div className="flex flex-col">
            {showOnlyChanges && !hasAnyChanges ? (
              <p className="paragraph-sm text-type-minor py-1">Изменений нет</p>
            ) : (
              <>
                <ResultRow label="Номер" isChanged={isContentChanged} showOnlyChanges={showOnlyChanges}>
                  <TextValue>{formatNumber(data.number)}</TextValue>
                </ResultRow>

                <ResultRow label="Название" isChanged={isContentChanged} showOnlyChanges={showOnlyChanges}>
                  <TextValue>{formatDisplayValue(data.title)}</TextValue>
                </ResultRow>

                <ResultRow label="Тип заявки" showOnlyChanges={showOnlyChanges}>
                  <TextValue>{formatDisplayValue(data.typeLabel)}</TextValue>
                </ResultRow>

                <ResultRow label="Инициатор" showOnlyChanges={showOnlyChanges}>
                  <TextValue>{formatDisplayValue(data.initiatorDisplay)}</TextValue>
                </ResultRow>

                <ResultRow label="Статус заявки" hasChanges={hasStatusChanges} isChanged={hasStatusChanges} showOnlyChanges={showOnlyChanges}>
                  <div className="flex flex-col">
                    {visibleStatuses.map((status) => (
                      <StatusValueRow key={status.id} item={status} />
                    ))}
                  </div>
                </ResultRow>

                <ResultRow label="Дорога" showOnlyChanges={showOnlyChanges}>
                  <TextValue>{formatDisplayValue(data.railway)}</TextValue>
                </ResultRow>

                <ResultRow label="Дата создания" showOnlyChanges={showOnlyChanges}>
                  <TextValue>{formatImportedAt(data.importedAt)}</TextValue>
                </ResultRow>

                <ResultRow label="Вложения" hasChanges={hasAttachmentChanges} isChanged={hasAttachmentChanges} showOnlyChanges={showOnlyChanges}>
                  <div className="flex flex-col">
                    {visibleAttachments.map((attachment) => (
                      <AttachmentRow key={attachment.id} attachment={attachment} />
                    ))}
                  </div>
                </ResultRow>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
